import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Globe2, LogIn } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { api, errMsg } from "../lib/api";
import { SiteSections } from "./SiteContent";
import { applyBrand } from "../lib/brand";
import { useApp } from "../context/AppContext";
import { BrandLogo, Btn, ErrorBox, Loading, PageHeader } from "../components/common";

export function RecoveryCodes({ codes, onDone }) {
  return (
    <div className="space-y-3" data-testid="recovery-codes">
      <p className="text-sm font-medium">Save these recovery codes now. Each works once if you lose your phone. They will not be shown again.</p>
      <div className="grid grid-cols-2 gap-2 rounded-md border bg-slate-50 p-3 font-mono text-sm">{codes.map((c) => <span key={c} data-testid="recovery-code">{c}</span>)}</div>
      <div className="flex gap-2"><Btn type="button" variant="outline" onClick={() => { navigator.clipboard?.writeText(codes.join("\n")); toast.success("Copied"); }} data-testid="recovery-copy">Copy</Btn>
        <Btn type="button" onClick={onDone} data-testid="recovery-done">I saved them</Btn></div>
    </div>
  );
}

export function AuthenticatorSetup({ onEnrolled, onConfirmed }) {
  const [s, setS] = useState(null);
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [codes, setCodes] = useState(null);
  const [res, setRes] = useState(null);
  const started = useRef(false);
  useEffect(() => { if (started.current) return; started.current = true; api.post("/auth/totp/setup").then((r) => setS(r.data)).catch((e) => setErr(errMsg(e))); }, []);
  const confirm = async (e) => {
    e.preventDefault(); setErr("");
    try { const r = await api.post("/auth/totp/confirm", { code }); setRes(r.data); setCodes(r.data.recovery_codes); onConfirmed?.(); } catch (e2) { setErr(errMsg(e2)); setCode(""); }
  };
  if (codes) return <RecoveryCodes codes={codes} onDone={() => onEnrolled(res)} />;
  if (!s) return err ? <p className="text-sm text-red-700" data-testid="totp-error">{err}</p> : <Loading />;
  return (
    <form onSubmit={confirm} className="space-y-3" data-testid="totp-setup">
      <p className="text-sm text-slate-600">1. Open an authenticator app (Google Authenticator, Microsoft Authenticator, Authy…) and scan this QR code.</p>
      <img src={s.qr} alt="Authenticator QR code" className="h-44 w-44 border rounded" data-testid="totp-qr" />
      <p className="text-xs text-slate-500">Can't scan? Enter this key manually: <span className="font-mono break-all" data-testid="totp-secret">{s.secret}</span></p>
      <label className="block text-sm">2. Enter the six-digit code shown in the app
        <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" className="mt-1 w-40 rounded-md border px-3 py-2 font-mono text-lg tracking-widest" data-testid="totp-code" /></label>
      {err && <p className="text-sm text-red-700" role="alert" data-testid="totp-error">{err}</p>}
      <Btn type="submit" disabled={code.length !== 6} data-testid="totp-confirm">Verify and turn on</Btn>
    </form>
  );
}

export function VerifyOtp() {
  const { status, reload } = useApp();
  const { state } = useLocation();
  const nav = useNavigate();
  const [digits, setDigits] = useState(Array(6).fill(""));
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState(null);
  const [rc, setRc] = useState(null);
  const refs = useRef([]);
  useEffect(() => { api.get("/auth/otp/pending").then((r) => setMethod(r.data.method)).catch((e) => { setMethod("expired"); setErr(errMsg(e)); }); }, []);
  if (status === "authed") return <Navigate to="/" replace />;
  const code = rc !== null ? rc.trim().toLowerCase() : digits.join("");
  const set = (i, v) => {
    const clean = v.replace(/\D/g, "");
    if (clean.length > 1) { const all = clean.slice(0, 6).split(""); setDigits([...all, ...Array(6 - all.length).fill("")]); refs.current[Math.min(all.length, 5)]?.focus(); return; }
    const n = [...digits]; n[i] = clean; setDigits(n);
    if (clean && i < 5) refs.current[i + 1]?.focus();
  };
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (rc === null && !/^\d{6}$/.test(code)) { setErr("Enter all six digits."); return; }
    setBusy(true);
    try { await api.post("/auth/otp/verify", { code }); await reload(); nav("/"); } catch (e2) { setErr(errMsg(e2)); setDigits(Array(6).fill("")); refs.current[0]?.focus(); } finally { setBusy(false); }
  };
  const cancel = async () => { await api.post("/auth/otp/cancel").catch(() => {}); nav(state?.back || "/login"); };
  const head = <div className="flex items-center gap-3 mb-6">{state?.branding && <BrandLogo branding={state.branding} size={44} testid="otp-logo" />}<p className="font-display font-bold">{state?.branding?.lgu_name || "BIMS"}</p></div>;
  if (!method) return <Loading />;
  if (method === "enroll") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6"><div className="w-full max-w-md rounded-lg border bg-white p-8 shadow-sm" data-testid="otp-enroll">{head}
        <h1 className="text-2xl font-bold flex items-center gap-2 mb-2"><ShieldCheck className="h-6 w-6 text-brand" />Set up two-factor authentication</h1>
        <p className="text-sm text-slate-600 mb-4">Your account needs an authenticator app before you can sign in.</p>
        <AuthenticatorSetup onEnrolled={async () => { await reload(); nav("/"); }} />
        <button type="button" onClick={cancel} className="mt-4 w-full text-sm text-slate-600 hover:underline" data-testid="otp-cancel-btn">Cancel and return to login</button></div></div>
    );
  }
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <form onSubmit={submit} className="w-full max-w-md rounded-lg border bg-white p-8 shadow-sm rise" data-testid="otp-form">
        {head}
        <h1 className="text-2xl font-bold flex items-center gap-2"><ShieldCheck className="h-6 w-6 text-brand" />Two-Factor Authentication</h1>
        <p className="mt-2 text-sm text-slate-600" data-testid="otp-instructions">{method === "totp" ? (rc !== null ? "Enter one of your saved recovery codes (format xxxx-xxxx)." : "Enter the six-digit code from your authenticator app.") : "Enter the six-digit verification code to complete your login."}</p>
        {method === "demo" && <p className="mt-2 text-xs rounded bg-amber-50 border border-amber-200 p-2 text-amber-900" data-testid="otp-demo-note">Demo Mode: Use the demonstration OTP configured for this environment.</p>}
        {rc !== null ? <input value={rc} onChange={(e) => setRc(e.target.value)} placeholder="xxxx-xxxx" className="mt-6 w-full rounded-md border px-3 py-2 font-mono" data-testid="otp-recovery-input" /> :
        <div className="mt-6 flex justify-between gap-2" role="group" aria-label="Six-digit code">
          {digits.map((d, i) => (
            <input key={i} ref={(el) => { refs.current[i] = el; }} value={d} inputMode="numeric" autoComplete={i === 0 ? "one-time-code" : "off"} maxLength={6} aria-label={`Digit ${i + 1}`}
              onChange={(e) => set(i, e.target.value)} onKeyDown={(e) => { if (e.key === "Backspace" && !d && i > 0) refs.current[i - 1]?.focus(); }}
              className="h-12 w-12 rounded-md border border-slate-300 text-center text-xl font-mono focus:border-[var(--brand-primary)] focus:outline-none" data-testid={`otp-digit-${i}`} />
          ))}
        </div>}
        {err && <p className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-2" role="alert" data-testid="otp-error">{err}</p>}
        <Btn type="submit" disabled={busy || method === "expired" || (rc === null ? code.length !== 6 : code.length !== 9)} className="w-full mt-6 !py-2.5" data-testid="otp-verify-btn">{busy ? "Verifying…" : "Verify"}</Btn>
        {method === "totp" && <button type="button" onClick={() => { setRc(rc === null ? "" : null); setErr(""); }} className="mt-3 w-full text-sm text-brand hover:underline" data-testid="otp-recovery-toggle">{rc === null ? "Use a recovery code instead" : "Use the authenticator app code"}</button>}
        <button type="button" onClick={cancel} className="mt-3 w-full text-sm text-slate-600 hover:underline" data-testid="otp-cancel-btn">Cancel and return to login</button>
      </form>
    </div>
  );
}

export function PublicSite({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp ?? params.slug ?? "";
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/site", { params: { slug } }).then((r) => { setS(r.data); if (r.data.branding) applyBrand(r.data.branding); }).catch(() => setS({ kind: "error" })); }, [slug]);
  if (!s) return <Loading />;
  if (s.kind === "central") return <Navigate to="/login" replace />;
  if (s.kind !== "barangay" || !s.branding) {
    return <div className="min-h-screen flex items-center justify-center p-6" data-testid="site-unavailable"><ErrorBox text={s.kind === "barangay" ? "This barangay website is currently unavailable." : s.kind === "removed" ? "This barangay site has been removed." : "This barangay site is not registered on BIMS."} /></div>;
  }
  const b = s.branding;
  const base = slug ? `/b/${slug}` : "";
  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-site">
      <header className="bg-brand text-white"><div className="mx-auto max-w-5xl px-6 py-10 flex items-center gap-4"><BrandLogo branding={b} size={72} testid="site-logo" />
        <div><h1 className="text-3xl sm:text-4xl font-bold" data-testid="site-name">{b.lgu_name}</h1><p className="opacity-90">{b.address}</p></div></div></header>
      <main className="mx-auto max-w-5xl px-6 py-10 grid md:grid-cols-3 gap-4">
        {[["Request a document", "Barangay clearance, certificates and other documents online.", `/portal?lgu=${s.slug}`],
          ["Track a request", "Check the status of your request with your reference and access code.", `/portal?lgu=${s.slug}`],
          ["Feedback & complaints", "Send feedback or report a concern to the barangay.", `/portal?lgu=${s.slug}`]].map(([t, d, to]) => (
          <Link key={t} to={to} className="rounded-md border bg-white p-5 hover:border-[var(--brand-primary)] transition-colors" data-testid={`site-link-${t.split(" ")[0].toLowerCase()}`}><Globe2 className="h-5 w-5 text-brand" /><p className="mt-2 font-semibold">{t}</p><p className="text-sm text-slate-600">{d}</p></Link>
        ))}
        <div className="md:col-span-3 rounded-md border bg-white p-5 text-sm text-slate-600">
          <p>Only information approved for public release is shown here. Resident records and internal documents are never published.</p>
        </div>
        <SiteSections slug={slug} />
        <Link to={`${base}/login`} className="md:col-span-3 inline-flex items-center gap-2 text-sm font-semibold text-brand" data-testid="site-staff-login"><LogIn className="h-4 w-4" />Barangay staff sign-in</Link>
      </main>
    </div>
  );
}

function PasswordDialog({ onClose, onSubmit }) {
  const [v, setV] = useState("");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="password-dialog">
        <DialogHeader><DialogTitle>Confirm with your password</DialogTitle><DialogDescription>This change affects how you sign in.</DialogDescription></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(v); }} className="space-y-3">
          <input type="password" autoFocus value={v} onChange={(e) => setV(e.target.value)} className="w-full rounded-md border px-3 py-2 text-sm" data-testid="password-dialog-input" />
          <div className="flex justify-end gap-2"><Btn type="button" variant="outline" onClick={onClose}>Cancel</Btn><Btn type="submit" disabled={!v} data-testid="password-dialog-submit">Confirm</Btn></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ProfileSettings() {
  const { meta } = useApp();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [setup, setSetup] = useState(false);
  const [codes, setCodes] = useState(null);
  const [pw, setPw] = useState(null);
  const load = () => api.get("/auth/otp").then((r) => setD(r.data)).catch((e) => setErr(errMsg(e)));
  useEffect(() => { load(); }, []);
  const withPw = async (password) => {
    const { url, okMsg } = pw;
    try { const r = await api.post(url, { password }); if (r.data.recovery_codes) setCodes(r.data.recovery_codes); else setD(r.data); toast.success(okMsg); setPw(null); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async () => {
    try { const r = await api.put("/auth/otp", { enabled: !d.enabled }); setD(r.data); toast.success("Your OTP login preference has been updated."); } catch (e) { toast.error(errMsg(e)); }
  };
  if (err) return <ErrorBox text={err} />;
  if (!d) return <Loading />;
  const on = d.enabled || d.mandatory;
  return (
    <div className="space-y-4 max-w-2xl" data-testid="profile-settings">
      <PageHeader crumbs={["Account"]} title="Profile settings" subtitle={`${meta.user.name} · ${meta.user.email}`} />
      <section className="rounded-md border bg-white p-5" data-testid="otp-settings">
        <h3 className="font-semibold">Two-Factor Authentication</h3>
        <p className="text-sm text-slate-600 mt-1">Add an additional verification step when logging in to your BIMS account.</p>
        <div className="mt-4 flex items-center justify-between">
          <div><p className="text-sm font-medium">Enable OTP Verification on Login</p><p className="text-xs text-slate-500" data-testid="otp-status">Status: {on ? "Enabled" : "Disabled"}{d.mandatory ? " — required by administrator policy" : ""}</p></div>
          <button role="switch" aria-checked={on} disabled={d.mandatory || d.totp_enrolled} onClick={toggle} data-testid="otp-toggle"
            className={`relative h-6 w-11 rounded-full transition-colors ${on ? "bg-[var(--brand-primary)]" : "bg-slate-300"} disabled:opacity-60`}>
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </div>
        {d.demo_mode && !d.totp_enrolled && <p className="mt-3 text-xs text-amber-800">Demonstration mode: a fixed code is accepted until you set up an authenticator app. It is not production-grade two-factor authentication.</p>}
      </section>
      <section className="rounded-md border bg-white p-5 space-y-3" data-testid="totp-settings">
        <h3 className="font-semibold">Authenticator app</h3>
        <p className="text-sm text-slate-600" data-testid="totp-status">{d.totp_enrolled ? `Set up · ${d.recovery_remaining} recovery codes left. Sign-in now asks for the code from your app.` : "Not set up. Use a free authenticator app on your phone for real two-factor sign-in."}</p>
        {codes ? <RecoveryCodes codes={codes} onDone={() => { setCodes(null); load(); }} /> : setup ? <AuthenticatorSetup onConfirmed={load} onEnrolled={() => { setSetup(false); load(); }} /> : d.totp_enrolled ? (
          <div className="flex flex-wrap gap-2"><Btn variant="outline" onClick={() => setPw({ url: "/auth/totp/recovery-codes", okMsg: "New recovery codes created" })} data-testid="totp-regen">New recovery codes</Btn>
            <Btn variant="outline" onClick={() => setPw({ url: "/auth/totp/disable", okMsg: "Authenticator app removed" })} data-testid="totp-remove">Remove authenticator</Btn></div>
        ) : <Btn onClick={() => setSetup(true)} data-testid="totp-start">Set up authenticator app</Btn>}
        {pw && <PasswordDialog onClose={() => setPw(null)} onSubmit={withPw} />}
      </section>
    </div>
  );
}
